import { FeedbackCategory } from "../../Feedback";
import {
  AppSubscriptionAction,
  AppSubscriptionActionType,
  IAppSubscriptionStatusLoadedActionPayload,
  ISetAppSubscriptionStatusErroredActionPayload
} from "../Actions/AppSubscriptionActions";

export interface AppSubscriptionsState {
  subscriptionStates: Record<string, AppSubscriptionState>;
}

export interface AppSubscriptionState {
  appName?: string;
  isSubscribed?: boolean;
  category?: FeedbackCategory;
  isLoading: boolean;
  isLoaded: boolean;
  isErrored: boolean;
  errorMessage?: string;
  errorCode?: number;
}

const initialAppSubscriptionsState: AppSubscriptionsState = {
  subscriptionStates: {} as Record<string, AppSubscriptionState>
};

const updateObject = (oldObject: any, newValues: any) => ({ ...oldObject, ...newValues });

// updates a single app subscription state in the dictionary immutably
function updateAppSubscriptionState(
  oldState: AppSubscriptionsState,
  appId: string,
  newValues: any
): AppSubscriptionsState {
  // clone the dictionary
  const newStates: Record<string, AppSubscriptionState> = updateObject(
    oldState.subscriptionStates,
    {}
  );

  let newState: AppSubscriptionState;
  if (newStates[appId] === undefined) {
    // create a new record
    newState = updateObject({ isLoaded: false, isLoading: false, isErrored: false }, newValues);
  } else {
    // clone the item we are going to change
    newState = updateObject(newStates[appId], newValues);
  }
  // update our cloned dictionary
  newStates[appId] = newState;

  // clone the full state and return
  return updateObject(oldState, { subscriptionStates: newStates });
}

const appSubscriptionReducer = (
  appSubscriptionsState: AppSubscriptionsState = initialAppSubscriptionsState,
  action: AppSubscriptionAction
) => {
  switch (action.type) {
    case AppSubscriptionActionType.APP_SUBSCRIPTION_LOADED: {
      const payload = <IAppSubscriptionStatusLoadedActionPayload>action.payload;
      return updateAppSubscriptionState(appSubscriptionsState, payload.appId, {
        appName: payload.appName,
        isSubscribed: payload.isSubscribed,
        category: payload.category,
        isLoading: false,
        isLoaded: true,
        isErrored: false
      });
    }
    case AppSubscriptionActionType.APP_SUBSCRIPTION_SET_LOADING: {
      return updateAppSubscriptionState(appSubscriptionsState, action.payload.appId, {
        appName: null,
        isSubscribed: null,
        category: null,
        isLoading: true,
        isLoaded: false,
        isErrored: false
      });
    }
    case AppSubscriptionActionType.APP_SUBSCRIPTION_UNLOAD: {
      return updateAppSubscriptionState(appSubscriptionsState, action.payload.appId, {
        appName: null,
        isSubscribed: null,
        category: null,
        isLoading: false,
        isLoaded: false,
        isErrored: false
      });
    }
    case AppSubscriptionActionType.APP_SUBSCRIPTION_SET_ERRORED: {
      const payload = <ISetAppSubscriptionStatusErroredActionPayload>action.payload;
      return updateAppSubscriptionState(appSubscriptionsState, action.payload.appId, {
        appName: null,
        isSubscribed: null,
        category: null,
        isLoading: false,
        isLoaded: false,
        isErrored: true,
        errorMessage: payload.errorMessage,
        errorCode: payload.errorCode
      });
    }
    default:
      return appSubscriptionsState;
  }
};

export default appSubscriptionReducer;
